import {
  canonicalSido,
  DeliveryPolicyForm,
  parseSidos,
  SIDO_OPTIONS,
} from '../utils/giftDeliveryPolicyForm';

const input = 'w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm';
export function GiftDeliveryPolicyEditor({
  value,
  onChange,
}: {
  value: DeliveryPolicyForm;
  onChange: (value: DeliveryPolicyForm) => void;
}) {
  const setSidos = (index: number, sidos: string[]) =>
    onChange({
      ...value,
      rules: value.rules.map((r, j) =>
        index === j ? { ...r, sidos: sidos.join(', ') } : r,
      ),
    });
  const toggle = (list: string[], sido: string) =>
    list.includes(sido) ? list.filter((v) => v !== sido) : [...list, sido];
  return (
    <div className="space-y-3 rounded-lg border border-indigo-100 bg-indigo-50 p-4">
      <label className="flex items-center gap-2 font-semibold">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
        />
        금액·지역 기준 배송비 사용
      </label>
      <p className="text-xs text-gray-600">
        사용하면 소량주문 개당 추가금은 적용하지 않으며, 한 배송지로 받는 일괄배송만
        가능합니다.
      </p>
      {value.enabled && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              기본배송비(원)
              <input
                className={input}
                type="number"
                min={0}
                step={1}
                value={value.fee}
                onChange={(e) => onChange({ ...value, fee: e.target.value })}
              />
            </label>
            <label className="text-sm">
              기본배송비 면제금액(원)
              <input
                className={input}
                type="number"
                min={0}
                step={1}
                value={value.freeOverAmount}
                onChange={(e) => onChange({ ...value, freeOverAmount: e.target.value })}
              />
            </label>
          </div>
          <p className="text-xs text-gray-600">
            해당 브랜드 상품금액(옵션 포함)이 면제금액 이상이면 기본배송비는 0원입니다.
            면제금액 0원은 항상 면제이며, 지역 추가배송비는 별도로 부과됩니다.
          </p>
          <div className="space-y-2">
            <p className="text-sm font-semibold">시도별 예외 요금</p>
            {value.rules.map((rule, i) => (
              <div
                key={i}
                className="grid items-end gap-2 rounded border border-indigo-100 bg-white p-3 sm:grid-cols-3"
              >
                <div className="text-xs sm:col-span-3">
                  <p>
                    적용 시도{' '}
                    <span className="text-gray-500">
                      (눌러서 선택 · 여러 개 가능)
                    </span>
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {SIDO_OPTIONS.map((sido) => {
                      const selected = parseSidos(rule.sidos).includes(sido);
                      const usedElsewhere = value.rules.some(
                        (r, j) => j !== i && parseSidos(r.sidos).includes(sido),
                      );
                      return (
                        <button
                          key={sido}
                          type="button"
                          aria-pressed={selected}
                          disabled={usedElsewhere && !selected}
                          title={
                            usedElsewhere && !selected
                              ? '다른 예외 요금에서 이미 선택한 시도입니다.'
                              : undefined
                          }
                          className={`rounded-full border px-3 py-1 text-sm ${
                            selected
                              ? 'border-indigo-600 bg-indigo-600 text-white'
                              : 'border-gray-300 bg-white text-gray-700'
                          } disabled:cursor-not-allowed disabled:opacity-40`}
                          onClick={() => setSidos(i, toggle(parseSidos(rule.sidos), sido))}
                        >
                          {sido}
                        </button>
                      );
                    })}
                  </div>
                  {parseSidos(rule.sidos)
                    .filter((s) => canonicalSido(s) === null)
                    .map((s) => (
                      <p key={s} className="mt-1 text-red-600">
                        인식할 수 없는 시도 "{s}" — 이 값으로는 예외 요금이 적용되지 않습니다.{' '}
                        <button
                          type="button"
                          className="underline"
                          onClick={() =>
                            setSidos(
                              i,
                              parseSidos(rule.sidos).filter((v) => v !== s),
                            )
                          }
                        >
                          삭제
                        </button>
                      </p>
                    ))}
                </div>
                <label className="text-xs">
                  기본배송비(원)
                  <input
                    className={input}
                    type="number"
                    min={0}
                    step={1}
                    value={rule.fee}
                    onChange={(e) =>
                      onChange({
                        ...value,
                        rules: value.rules.map((r, j) =>
                          i === j ? { ...r, fee: e.target.value } : r,
                        ),
                      })
                    }
                  />
                </label>
                <label className="text-xs">
                  면제금액(원)
                  <input
                    className={input}
                    type="number"
                    min={0}
                    step={1}
                    value={rule.freeOverAmount}
                    onChange={(e) =>
                      onChange({
                        ...value,
                        rules: value.rules.map((r, j) =>
                          i === j ? { ...r, freeOverAmount: e.target.value } : r,
                        ),
                      })
                    }
                  />
                </label>
                <button
                  type="button"
                  className="rounded border px-3 py-2 text-sm"
                  onClick={() =>
                    onChange({ ...value, rules: value.rules.filter((_, j) => i !== j) })
                  }
                >
                  예외 삭제
                </button>
              </div>
            ))}
            <button
              type="button"
              className="rounded border border-indigo-200 bg-white px-3 py-2 text-sm"
              onClick={() =>
                onChange({
                  ...value,
                  rules: [...value.rules, { sidos: '', fee: '', freeOverAmount: '' }],
                })
              }
            >
              시도별 예외 추가
            </button>
          </div>
          <label className="block text-sm">
            등록되지 않은 지역
            <select
              className={input}
              value={value.unlistedZone}
              onChange={(e) =>
                onChange({ ...value, unlistedZone: e.target.value as 'reject' | 'allow' })
              }
            >
              <option value="reject">배송불가 (등록한 지역만 배송)</option>
              <option value="allow">배송가능 (추가배송비 0원)</option>
            </select>
          </label>
          {value.unlistedZone === 'allow' && (
            <p className="text-sm text-amber-800">
              미등록 지역에도 주문을 허용합니다. 특정 지역을 막으려면 배송불가로
              등록하세요.
            </p>
          )}
        </>
      )}
    </div>
  );
}
